/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import { DataType, Field, Item, Suburb } from '@api';
import moment from 'moment';
import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import * as Form from 'redux-form';
import { clearAsyncError, unregisterField } from 'redux-form';
import { FileField } from 'src/js/components/form/FileField';
import {
  validateDate,
  validateDateTime,
  validateEmail,
  validatePattern,
  validateURL
} from '../../common/utils/FormControlsValidation';
import { replaceWithNl } from '../../common/utils/HtmlUtils';
import SearchService from '../../enrol/services/SearchService';
import SelectField from '../form-new/SelectField';
import { CheckboxField } from './CheckboxField';
import { DateField } from './DateField';
import { DateTimeField } from './DateTimeField';
import { MoneyField } from './MoneyField';
import { TextAreaField } from './TextAreaField';
import { TextField } from './TextField';

const FieldFactory = (
  {
    field, index, dispatch, form, onBlurSelect, onChangeSuburb
  }) => {

  const props: any = toFormFieldProps(field, index);
  props.onBlurSelect = onBlurSelect;
  props.onChangeSuburb = onChangeSuburb;
  props.validate = [];

  useEffect(() => {
    return () => {
      const props: any = toFormFieldProps(field, index);
      // Force field uregister to remove errors from state
      dispatch(unregisterField(form, props.name));
      dispatch(clearAsyncError(form, props.name));
    }
  }, [])

  const validatePatternInner = useCallback((val) => validatePattern(val, field?.pattern), [field?.pattern]);

  const validatePossibleValues = useCallback((val) => {
    const possibleValuesFormatted = field.possibleValues.map(v => v.toLowerCase())

    let valueFormatted = typeof val === 'object' && val.value ? val.value : val;

    return possibleValuesFormatted.includes(valueFormatted?.toLowerCase()) ? undefined : `${props.label || 'Value'} should match original`
  }, [field?.possibleValues, props.label]);

  const getComponent = () => {
    if (field.possibleValues?.length) {
      props.validate.push(
        validatePossibleValues
      )
    }

    switch (field.dataType) {
      case DataType.FILE:
        return <Form.Field {...props} component={FileField} type="text" />;
      case DataType.STRING:
        return <Form.Field {...props} component={TextField} type="text" />;
      case DataType.PHONE:
        return <Form.Field {...props} component={TextField} type="text" autocomplete="tel" />;
      case DataType.POSTCODE:
        return <Form.Field {...props} component={TextField} type="text" autocomplete="postal-code" />;
      case DataType.PATTERN_TEXT:
        props.validate.push(validatePatternInner);
        return (
          <Form.Field
            {...props}
            component={TextField}
            type="text"
          />
        );
      case DataType.EMAIL:
        props.validate.push(validateEmail);
        return <Form.Field {...props} component={TextField} autocomplete="email" type="text" />;
      case DataType.URL:
        props.validate.push(validateURL);
        return <Form.Field {...props} component={TextField} autocomplete="url" type="text" />;
      case DataType.LONG_STRING:
        return <Form.Field {...props} component={TextAreaField} type="text" />;
      case DataType.INTEGER:
        return <Form.Field {...props} component={TextField} type="number" />;
      case DataType.MONEY:
        return <Form.Field {...props} component={MoneyField} type="number" />;
      case DataType.ENUM:
        return (
          <Form.Field
            {...props}
            hint={props.placeholder}
            component={SelectField}
            loadOptions={() => Promise.resolve(field.enumItems)}
            newOptionEnable={false}
            searchable={false}
            returnType="object"
            placeholder="Please Select..."
          />
        );

      case DataType.BOOLEAN:
        return (
          <Form.Field
            {...props}
            component={CheckboxField}
          />
        );
      case DataType.DATE:
        props.validate.push(validateDate);
        return <Form.Field {...props} component={DateField} />;

      case DataType.DATETIME:
        props.validate.push(validateDateTime);
        return <Form.Field {...props} component={DateTimeField} />;

      case DataType.COUNTRY:
        return CountryField(props);

      case DataType.SUBURB:
        return <SuburbField {...props} form={form} />;

      case DataType.LANGUAGE:
        return LanguageField(props);

      case DataType.CHOICE:
        return (
          <Form.Field
            {...props}
            component={SelectField}
            loadOptions={() => Promise.resolve(withOptionOther(field.enumItems))}
            newOptionEnable
            searchable
            showOnFocus
            placeholder="Please Select..."
          />
        );

      case DataType.TAGGROUP_S:
        return (
          <Form.Field
            {...props}
            hint={props.label}
            component={SelectField}
            loadOptions={() => Promise.resolve(withOptionNotSet(field.enumItems))}
            newOptionEnable
            searchable
            showOnFocus
            fullWidth
          />
        );

      case DataType.TAGGROUP_M:
        return (
          <Form.Field
            {...props}
            hint={props.label}
            component={CheckboxField}
          />
        );

      case DataType.MAILINGLIST:
        return (
          <Form.Field
            {...props}
            hint={props.label}
            required={false}
            component={CheckboxField}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {getComponent()}
    </div>
  );
}

export const toFormFieldProps = (field: Field, index: number): any => ({
  key: field.id,
  name: toFormKey(field.key, index),
  label: replaceWithNl(field.name),
  type: 'text',
  required: field.mandatory,
  pattern: field.pattern,
  placeholder: replaceWithNl(field.description),
});

// replace all dots to slashes, b/c redux form converts it to object
export const toFormKey = (name: string, index: number) => name.replace(/\./g, '/') + index;

// read file
export const getDocumentContent = (file) => {
  const reader = new FileReader();
  return new Promise(resolve => {
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  }).then((result: any) => result.replace(`data:${file.type};base64,`, ''));
};

// format fields values to valid for server side
export const toServerValues = async (fields: Field[], values: { [key: string]: any }) => {
  let index = 0;
  for (const f of fields) {
    const formKey = toFormKey(f.key, index);
    const value = values[formKey];

    f.value = value && value.key || value || null;
    f.itemValue = null;

    switch (f.dataType) {
      case DataType.FILE: {
        if (value instanceof File) {
          f.fileValue = {
            name: value.name,
            content: await getDocumentContent(value)
          };
        } else if (value instanceof Object) {
          f.fileValue = null;
        }
        f.value = null;
        break;
      }

      case DataType.SUBURB: {
        if (value && value.suburb) {
          const suburb: Suburb = {
            postcode: value.postcode,
            state: value.state,
            suburb: value.suburb,
          };

          f.value = null;
          f.itemValue = { key: value.key, value: suburb };
        }
        break;
      }
      case DataType.DATE: {
        if (value) {
          f.value = moment(value, 'DD/MM/YYYY').format('YYYY-MM-DD');
        }
        break;
      }
      case DataType.DATETIME: {
        if (value) {
          f.value = moment(value, 'DD/MM/YYYY hh:mm').toISOString();
        }
        break;
      }
      case DataType.MONEY: {
        if (value) {
          f.value = parseFloat(value.replace(/[$,]/g, ''))?.toString();
        }
        break;
      }
    }

    index++;
  }
};

const SuburbFieldBase = (props): any => {
  const suburbs = (i: string): Promise<Item[]> => SearchService.getPreparedSuburbs(i);
  const updateRelativeFields = (...args) => {
    props.onChangeSuburb && props.onChangeSuburb(...args);
  };

  const isAustralia = props.values && (!props.values.country || props.values.country === 'Australia');

  return isAustralia
    ? (
      <Form.Field
        {...props}
        component={SelectField}
        loadOptions={suburbs}
        newOptionEnable
        allowEditSelected
        returnType="object"
        onChange={(val) => updateRelativeFields(val)}
      />
    )
    : <Form.Field {...props} component={TextField} type="text" />;
};

const SuburbField = connect<any, any, any>((state, { form }) => ({
  values: Form.getFormValues(form)(state),
}))(SuburbFieldBase);

const CountryField = (props): any => {
  const countries = (i: string): Promise<Item[]> => SearchService.getCountries(i);

  return (
    <Form.Field
      {...props}
      component={SelectField}
      loadOptions={countries}
    />
  );
};

const LanguageField = (props): any => {
  const langs = (i: string): Promise<Item[]> => SearchService.getLanguages(i);
  return (
    <Form.Field
      {...props}
      component={SelectField}
      loadOptions={langs}
    />
  );
};

const withOptionOther = (items) => items.concat({
  key: null,
  value: 'Other',
});

const withOptionNotSet = (items) => items.concat({
  key: null,
  value: 'Not set',
});

export const getFormInitialValues = (headings) => {
  const initialValues = {};

  if (headings && headings.length) {
    headings
      .forEach((h) => h.fields
        .forEach((f, index) => {
          if (f.defaultValue) {
            initialValues[toFormKey(f.key, index)] = f.dataType === 'ENUM'
              ? f.enumItems.find((en) => en.key === f.defaultValue)
              : f.dataType === 'BOOLEAN'
                ? JSON.parse(f.defaultValue)
                : f.defaultValue;
          }
        }));

    return initialValues;
  }

  return null;
};

export default FieldFactory;